import { useCallback, useInsertionEffect, useRef } from 'react';

/**
 * Returns a stable callback that will not change references between renders.
 * It's like useCallback, but without the need for a dependency array.
 *
 * Only call the returned function from event handlers or effects. The latest callback is
 * swapped in after render, so a call made during render runs the previous render's callback.
 *
 * Example -
 * ```javascript
 *   const onClick = useStableCallback((e) => {
 *     console.log('clicked', e);
 *   })
 *   return <button onClick={onClick}>Btn</button>
 * ```
 */
export const useStableCallback = <Args extends any[], Ret>(callback: (...a: Args) => Ret): ((...a: Args) => Ret) => {
  const cb = useRef(callback);

  useInsertionEffect(() => {
    cb.current = callback;
  });

  return useCallback((...args: Args) => cb.current(...args), []);
};
